import React, { useCallback } from 'react';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import { Box, TextField } from '@mui/material';
import { useIntl } from 'react-intl';
import { MemoAnswerCardHeader } from './AnswerCardHeader';
import { AnswerPanelProps, TextAnswerConfig } from './types';
import { getAnswerCardSx } from './answerTypeTheme';
import { formatFieldError } from './formatFieldError';

export function TextAnswerPanel({
  answerId,
  answer,
  fieldErrors,
  onUpdate,
  onDelete,
}: AnswerPanelProps<TextAnswerConfig>): React.ReactElement {
  const intl = useIntl();

  const handleUpdate = useCallback(
    (updates: Partial<TextAnswerConfig>) => {
      onUpdate(answerId, updates);
    },
    [answerId, onUpdate]
  );

  const handleDelete = useCallback(() => {
    onDelete(answerId);
  }, [answerId, onDelete]);

  const handleLabelChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleUpdate({ label: event.target.value });
    },
    [handleUpdate]
  );

  const handleRequiredChange = useCallback(
    (isRequired: boolean) => {
      handleUpdate({ isRequired });
    },
    [handleUpdate]
  );

  return (
    <Box sx={getAnswerCardSx(answer.type)}>
      <MemoAnswerCardHeader
        answerType={answer.type}
        isRequired={answer.isRequired}
        onRequiredChange={handleRequiredChange}
        onDelete={handleDelete}
      />

      <TextField
        label={formatTranslation(intl, InspectionTranslationKeys.label)}
        value={answer.label}
        onChange={handleLabelChange}
        size="medium"
        fullWidth
        required
        error={Boolean(fieldErrors?.label)}
        helperText={formatFieldError(intl, fieldErrors?.label)}
      />
    </Box>
  );
}

export const MemoTextAnswerPanel = React.memo(TextAnswerPanel);
